import { useAuthStore } from '@/features/auth/auth.store';

/**
 * As permissões desta sessão, lidas do token — **para afordância, nunca para
 * controle**.
 *
 * ⚠ Isto não protege nada, e não deve dar a impressão de proteger. O token é
 * assinado, mas quem o lê aqui é o navegador do usuário, que também pode
 * mentir para si mesmo. Quem decide é o servidor: `KnowledgeService.transition`
 * confere a permissão da transição pedida antes de gravar, e um `PATCH` de
 * `visibility` que alargue o público de um artigo no ar exige
 * `knowledge.publish` lá, não aqui.
 *
 * O que isto resolve é outra coisa: mostrar "Aprovar" e "Publicar" a um técnico
 * que só tem `knowledge.create` faz ele clicar e receber 403. Uma tela que
 * oferece o que vai recusar não ensina o processo — ensina a desconfiar dela.
 *
 * Mora nesta feature, e não no store de autenticação, de propósito: o store é
 * compartilhado e acrescentar um campo a ele obriga o login, o refresh e a
 * persistência a concordarem sobre mais uma coisa. Aqui é uma leitura derivada
 * do que já está lá.
 */

interface ClaimsDoToken {
  permissions?: unknown;
}

/**
 * Lê a carga do JWT sem verificar a assinatura — e isso está certo aqui.
 *
 * Verificar exigiria a chave pública no navegador e não mudaria nada: o
 * resultado só liga e desliga botão. A verificação de verdade acontece a cada
 * requisição, no servidor.
 */
function permissoesDoToken(token: string | null): string[] {
  if (!token) return [];
  const carga = token.split('.')[1];
  if (!carga) return [];
  try {
    // base64url → base64. `atob` não aceita `-` nem `_`.
    const base64 = carga.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join(''),
    );
    const claims = JSON.parse(json) as ClaimsDoToken;
    return Array.isArray(claims.permissions)
      ? claims.permissions.filter((p): p is string => typeof p === 'string')
      : [];
  } catch {
    // Token malformado: nenhuma permissão. A tela fica pobre e o servidor
    // continua respondendo o que tiver de responder.
    return [];
  }
}

/** `true` quando a sessão declara a permissão — ou o curinga. */
export function usePode(): (codigo: string) => boolean {
  const token = useAuthStore((estado) => estado.accessToken);
  const permissoes = permissoesDoToken(token);
  return (codigo) => permissoes.includes('*') || permissoes.includes(codigo);
}

export { permissoesDoToken };
